import type {
  AppointmentInput,
  AppointmentStatusValue,
  PaymentInput,
  UpdateAppointmentInput,
} from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useBookingMode } from './booking-mode';

export const SCHEDULE_ROUTES = {
  AGENDA: '/agenda',
  NEW: '/agenda/novo',
  DETAIL_PATTERN: '/agenda/:id',
  detail: (id: string) => `/agenda/${id}`,
  SERVICE_DESK: '/atendimentos',
} as const;

const ENDPOINT = '/api/v1/appointments';

export interface Appointment {
  id: string;
  companyId: string;
  unitId: string;
  unitName: string;
  customerId: string;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  customerImage: string | null;
  items: {
    id: string;
    serviceId: string;
    serviceName: string;
    serviceImage: string | null;
    professionalId: string | null;
    professionalName: string | null;
    professionalImage: string | null;
    startAt: string;
    price: number;
    durationMinutes: number;
    discountPercent: number;
    finalPrice: number;
  }[];
  /** Rotulo curto para a agenda. */
  serviceSummary: string;
  professionalId: string | null;
  professionalName: string | null;
  /** Marcado como "tanto faz quem atende" - da para trocar sem desmarcar. */
  anyProfessional: boolean;
  startAt: string;
  endAt: string;
  durationMinutes: number;
  status: AppointmentStatusValue;
  subtotal: number;
  discountTotal: number;
  /** Cupom aplicado e quanto ele abateu. */
  couponId: string | null;
  couponCode: string | null;
  couponDiscount: number;
  /** Cashback do cliente abatido nesta compra. */
  cashbackUsed: number;
  finalPrice: number;
  notes: string | null;
  cancelReason: string | null;
  checkedInAt: string | null;
  checkedOutAt: string | null;
  /** Ultima remarcacao do horario; nulo enquanto nunca foi remarcado. */
  rescheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgendaQuery {
  from: Date;
  to: Date;
  unitId?: string;
  status?: AppointmentStatusValue;
  /** Filtros da fila do dia: por quem atende e por quem e atendido. */
  professionalId?: string;
  customerId?: string;
}

/** Um profissional na escolha de horario. */
export interface Professional {
  id: string;
  name: string;
  image: string | null;
}

export interface AvailabilityDay {
  /** Data local, YYYY-MM-DD. */
  date: string;
  /** Expediente inteiro do dia; `available` diz o que da para marcar. */
  slots: { start: string; available: boolean }[];
}

export const scheduleService = {
  list(params: AgendaQuery) {
    const query = new URLSearchParams({
      from: params.from.toISOString(),
      to: params.to.toISOString(),
    });

    if (params.unitId) query.set('unitId', params.unitId);
    if (params.status) query.set('status', params.status);
    if (params.professionalId) query.set('professionalId', params.professionalId);
    if (params.customerId) query.set('customerId', params.customerId);

    return apiClient.get<{ data: Appointment[] }>(`${ENDPOINT}?${query.toString()}`);
  },

  // Com `slug`, a consulta vai pelas rotas publicas: mesma resposta, sem sessao.
  professionals(unitId?: string, slug?: string) {
    const query = new URLSearchParams();
    if (unitId) query.set('unitId', unitId);
    if (slug) query.set('slug', slug);

    const path = slug ? '/api/v1/public/professionals' : `${ENDPOINT}/professionals`;
    const search = query.toString();

    return apiClient.get<{ data: Professional[] }>(search ? `${path}?${search}` : path);
  },

  availability(params: {
    professionalId: string;
    from: Date;
    to: Date;
    durationMinutes: number;
    ignoreId?: string;
    slug?: string;
  }) {
    const query = new URLSearchParams({
      professionalId: params.professionalId,
      from: params.from.toISOString(),
      to: params.to.toISOString(),
      durationMinutes: String(params.durationMinutes),
    });

    if (params.ignoreId) query.set('ignoreId', params.ignoreId);
    if (params.slug) query.set('slug', params.slug);

    const path = params.slug ? '/api/v1/public/availability' : `${ENDPOINT}/availability`;

    return apiClient.get<{ days: AvailabilityDay[] }>(`${path}?${query.toString()}`);
  },

  get: (id: string) => apiClient.get<Appointment>(`${ENDPOINT}/${id}`),
  create: (input: AppointmentInput) => apiClient.post<Appointment>(ENDPOINT, input),
  update: (id: string, input: UpdateAppointmentInput) =>
    apiClient.patch<Appointment>(`${ENDPOINT}/${id}`, input),
  setNotes: (id: string, notes: string) =>
    apiClient.patch<Appointment>(`${ENDPOINT}/${id}/notes`, { notes }),
  confirm: (id: string) => apiClient.post<Appointment>(`${ENDPOINT}/${id}/confirm`),
  /** Avisa o cliente que o horario esta marcado - o "Finalizar" da tela. */
  notify: (id: string) => apiClient.post<Appointment>(`${ENDPOINT}/${id}/notify`),
  cancel: (id: string, reason?: string) =>
    apiClient.post<Appointment>(`${ENDPOINT}/${id}/cancel`, { reason }),
  checkIn: (id: string) => apiClient.post<Appointment>(`${ENDPOINT}/${id}/check-in`),
  /** Encerrar leva junto como o cliente pagou - e dai saem os recebiveis. */
  checkOut: (id: string, payments: PaymentInput[] = []) =>
    apiClient.post<Appointment>(`${ENDPOINT}/${id}/check-out`, { payments }),
  noShow: (id: string) => apiClient.post<Appointment>(`${ENDPOINT}/${id}/no-show`),
};

const KEYS = { all: ['appointments'] as const };

export function useAgenda(params: AgendaQuery) {
  return useQuery({
    queryKey: [
      ...KEYS.all,
      params.from.toISOString(),
      params.to.toISOString(),
      params.unitId ?? '',
      params.status ?? '',
    ],
    queryFn: () => scheduleService.list(params),
    placeholderData: (previous) => previous,
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: [...KEYS.all, 'detail', id ?? ''],
    queryFn: () => scheduleService.get(id as string),
    enabled: Boolean(id),
  });
}

export function useProfessionals(unitId?: string, enabled = true) {
  const mode = useBookingMode();

  return useQuery({
    queryKey: [...KEYS.all, 'professionals', unitId ?? '', mode?.slug ?? ''],
    queryFn: () => scheduleService.professionals(unitId, mode?.slug),
    enabled,
  });
}

/** Horarios livres do profissional no mes visivel da modal. */
export function useAvailability(params: {
  professionalId: string | undefined;
  /** Uniao dos horarios de todo mundo da unidade. */
  anyProfessional?: boolean;
  unitId?: string;
  from: Date;
  to: Date;
  durationMinutes: number;
  ignoreId?: string;
}) {
  const mode = useBookingMode();

  return useQuery({
    queryKey: [
      ...KEYS.all,
      'availability',
      params.professionalId ?? '',
      params.anyProfessional ? 'qualquer' : '',
      params.unitId ?? '',
      params.from.toISOString(),
      params.to.toISOString(),
      params.durationMinutes,
      params.ignoreId ?? '',
      mode?.slug ?? '',
    ],
    queryFn: () =>
      scheduleService.availability({
        professionalId: params.professionalId as string,
        ...(params.anyProfessional ? { anyProfessional: true, unitId: params.unitId } : {}),
        from: params.from,
        to: params.to,
        durationMinutes: params.durationMinutes,
        ignoreId: params.ignoreId,
        slug: mode?.slug,
      }),
    enabled: Boolean(params.professionalId),
    placeholderData: (previous) => previous,
  });
}

function useAppointmentMutation<TArgs, TResult = unknown>(
  mutationFn: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useCreateAppointment() {
  return useAppointmentMutation((input: AppointmentInput) => scheduleService.create(input));
}

export function useUpdateAppointment() {
  return useAppointmentMutation(({ id, input }: { id: string; input: UpdateAppointmentInput }) =>
    scheduleService.update(id, input),
  );
}

/** Confirmar, cancelar, iniciar, encerrar e marcar desistencia. */
export function useAppointmentAction() {
  return useAppointmentMutation(
    ({
      id,
      action,
      reason,
      payments,
    }: {
      id: string;
      action: 'confirm' | 'cancel' | 'checkIn' | 'checkOut' | 'noShow' | 'notify';
      reason?: string;
      payments?: PaymentInput[];
    }) => {
      if (action === 'cancel') return scheduleService.cancel(id, reason);
      if (action === 'checkOut') return scheduleService.checkOut(id, payments);

      return scheduleService[action](id);
    },
  );
}

export function useSaveNotes() {
  return useAppointmentMutation(({ id, notes }: { id: string; notes: string }) =>
    scheduleService.setNotes(id, notes),
  );
}
